// src/components/ErrorBoundary.tsx
import React, { ErrorInfo, ReactNode } from 'react'
import { Modal, Button } from 'react-bootstrap'
import styled from 'styled-components'

const StyledModal = styled(Modal)`
  .modal-content {
    background: #1a1a1a;
    border: none;
  }

  .modal-header {
    background: linear-gradient(45deg, #402563, #114ee0);
    border-bottom: none;
    padding: 1.25rem 1.5rem;

    .modal-title {
      color: white;
      font-weight: 600;
      font-size: 1.25rem;
    }
  }

  .modal-body {
    background: #2a2a2a;
    color: #fff;
    padding: 1.5rem;
  }

  .error-details {
    background: #1a1a1a;
    padding: 1rem;
    border-radius: 4px;
    font-family: monospace;
    white-space: pre-wrap;
    max-height: 200px;
    overflow-y: auto;
    margin-top: 1rem;
  }

  .modal-footer {
    background: #2a2a2a;
    border-top: 1px solid rgba(255, 255, 255, 0.1);

    .btn {
      min-width: 120px;
    }
  }
`

interface ErrorBoundaryProps {
  children: ReactNode
  testMode?: boolean
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    console.log('ErrorBoundary constructor, testMode:', props.testMode)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    console.log('ErrorBoundary.getDerivedStateFromError called with:', error.message)
    // This is called during render phase, immediately capture the error
    return {
      hasError: true,
      error: error,
      errorInfo: null,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.log('ErrorBoundary.componentDidCatch called, testMode:', this.props.testMode)
    // This is called during commit phase
    this.setState({
      hasError: true,
      error: error,
      errorInfo: errorInfo,
    })

    // Log the error
    console.log('Application Error:', error.message)
    console.log('Component Stack:', errorInfo.componentStack)
  }

  handleClose = () => {
    console.log('ErrorBoundary.handleClose called')
    this.setState(
      {
        hasError: false,
        error: null,
        errorInfo: null,
      },
      () => {
        // Only redirect if not in test mode
        if (!this.props.testMode) {
          console.log('Redirecting to /projects')
          window.location.href = '/projects'
        } else {
          console.log('In test mode - not redirecting on close')
          // Just reload the current page in test mode
          window.location.reload()
        }
      }
    )
  }

  handleCopyError = () => {
    console.log('ErrorBoundary.handleCopyError called')
    const { error, errorInfo } = this.state
    const errorText = `Error: ${error?.message}\n\nStack Trace:\n${errorInfo?.componentStack}`
    navigator.clipboard.writeText(errorText).then(() => {
      console.log('Error details copied to clipboard')
    })
  }

  handleReload = () => {
    console.log('ErrorBoundary.handleReload called')
    this.setState(
      {
        hasError: false,
        error: null,
        errorInfo: null,
      },
      () => {
        window.location.reload()
      }
    )
  }

  render() {
    if (this.state.hasError && this.state.error) {
      console.log('ErrorBoundary rendering error UI, testMode:', this.props.testMode)
      const errorDetails = `${this.state.error?.message}\n\n${
        this.state.errorInfo?.componentStack || ''
      }`

      return (
        <StyledModal show={true} backdrop="static" keyboard={false}>
          <Modal.Header>
            <Modal.Title>
              {this.props.testMode ? 'Test Error Detected' : 'An Error Occurred'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.props.testMode ? (
              <p>This is a test error. The error boundary is working correctly!</p>
            ) : (
              <>
                <p>
                  We apologize, but something went wrong. Please use the buttons below to copy the
                  details and notify our team. After copying the details, please reload the page, or
                  go back to the projects page.
                </p>
                <p>
                  There is likely an unexpected issue with the data being used to render the page
                  you are trying to access and any details you can provide will help us resolve the
                  issue.
                </p>
              </>
            )}
            <p>You can:</p>
            <ul>
              <li>Reload the page</li>
              <li>Go back to the projects page</li>
              <li>Copy the error details for the tech team</li>
            </ul>
            <div className="error-details">{errorDetails}</div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <div>
              <Button variant="outline-primary" onClick={this.handleReload} className="me-2">
                Reload Page
              </Button>
              <Button variant="outline-secondary" onClick={this.handleClose}>
                {this.props.testMode ? 'Reload Page' : 'Go to Projects'}
              </Button>
            </div>
            <Button variant="primary" onClick={this.handleCopyError}>
              Copy Error Details
            </Button>
          </Modal.Footer>
        </StyledModal>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
